import { Box, Card, Collapse, Divider, Link, Text } from '@ksyos/design-system';
import { useState } from 'react';

type InvoicingNotContractedCareProps = {
    insurerName?: string | null;
};

export function InvoicingNotContractedCare(props: Readonly<InvoicingNotContractedCareProps>) {
    const { insurerName } = props;
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <Card>
                <Card.Body>
                    <Box maxWidth={612}>
                        {`Volgens onze gegevens ben jij verzekerd bij ${insurerName}. Ksyos heeft geen contract met deze zorgverzekeraar. Lees hieronder wat jij moet doen.`}
                    </Box>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={6}
                        maxWidth={612}
                    >
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="row"
                            gap={6}
                        >
                            <img
                                alt="email-post"
                                src="/assets/images/somnology/insurance/Download_rekening_MK.svg"
                                style={{ width: '67px' }}
                            />
                            <Box>
                                <Text>
                                    Ksyos stuurt de rekening(en) (factuur) naar jou via e-mail. Je hebt <b>30 dagen</b>{' '}
                                    de tijd om de rekening te betalen.
                                </Text>
                            </Box>
                        </Box>
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="row"
                            gap={6}
                        >
                            <img
                                alt="email-post"
                                src="/assets/images/somnology/insurance/Declareren_MK.svg"
                                style={{ width: '67px' }}
                            />
                            <Text>
                                Jij dient deze rekening <b>direct</b> in bij jouw zorgverzekeraar. Meestal kan dit via
                                de website of via een app.
                            </Text>
                        </Box>

                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="row"
                            gap={6}
                        >
                            <img
                                alt="factuur"
                                src="/assets/images/somnology/insurance/Factuur_MK.svg"
                                style={{ width: '67px' }}
                            />
                            <Text>
                                Jouw zorgverzekeraar stuurt jou een document met de kosten die gemaakt zijn voor de
                                zorg. Jouw zorgverzekeraar betaalt jou mogelijk een bedrag uit.
                            </Text>
                        </Box>

                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="row"
                            gap={6}
                        >
                            <img
                                alt="vergoeding"
                                src="/assets/images/somnology/insurance/Upload_doc.svg"
                                style={{ width: '67px' }}
                            />
                            <Text>
                                Jij uploadt dit document in het online factuurdossier. De link naar je factuurdossier
                                vind je in de e-mail die je van Ksyos hebt ontvangen.
                            </Text>
                        </Box>

                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="row"
                            gap={6}
                        >
                            <img
                                alt="patient-betaalt"
                                src="/assets/images/somnology/insurance/Vergoeding_MK.svg"
                                style={{ width: '67px' }}
                            />
                            <Box>
                                <Text>
                                    Jij betaalt aan Ksyos: het bedrag dat jij ontvangt van je zorgverzekeraar + je
                                    openstaand eigen risico.
                                </Text>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        maxWidth={612}
                        mt={8}
                    >
                        <Collapse isOpen={isOpen}>
                            <Text pb={6}>
                                In het document van jouw zorgverzekeraar staat een overzicht van de kosten. Hierin staat
                                welke vergoeding jij krijgt. Van deze vergoeding halen ze eerst het bedrag aan eigen
                                risico af. De rest van de vergoeding maakt jouw zorgverzekeraar naar jou over. Hieronder
                                zie je een voorbeeld van de kosten.
                            </Text>
                            <Text pb={6}>
                                <b>Let op:</b> de kosten verschillen per persoon en behandeling.
                            </Text>
                            <Box
                                backgroundColor="neutral50"
                                padding={4}
                            >
                                <Box
                                    pl={4}
                                    pr={4}
                                >
                                    <Text
                                        fontWeight="bold"
                                        pb={4}
                                    >
                                        Voorbeeld
                                    </Text>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={2}
                                    >
                                        <Text fontWeight="bold">Document met de kosten van jouw zorgverzekeraar</Text>
                                        <Text>Totaal: €800 </Text>
                                        <Text pl={4}>Vergoed: €480</Text>
                                        <Text pl={4}>Niet vergoed: €320</Text>
                                        <Text pl={4}>Eigen risico: €235</Text>
                                        <Text
                                            as="span"
                                            pl={4}
                                        >
                                            Jij ontvangt:{' '}
                                            <Text
                                                as="span"
                                                color="success"
                                                fontWeight="bold"
                                            >
                                                €245
                                            </Text>
                                        </Text>
                                        <Divider spacing="small" />

                                        <Text fontWeight="bold"> Jij betaalt aan Ksyos</Text>
                                        <Text>Ontvangen bedrag: €245 </Text>
                                        <Text>Eigen risico: €235 </Text>
                                    </Box>
                                    <Box maxWidth={180}>
                                        <Text
                                            lineHeight={3}
                                            textAlign="right"
                                        >
                                            +
                                        </Text>
                                    </Box>
                                    <Box maxWidth={172}>
                                        <Divider spacing="small" />
                                    </Box>
                                </Box>
                                <Box
                                    backgroundColor="white"
                                    padding={2}
                                >
                                    <Text fontWeight="bold">Betalen: €480</Text>
                                </Box>
                            </Box>

                            <Text
                                fontSize={'sm'}
                                pt={6}
                            >
                                Zie voor meer informatie onze{' '}
                                <Link
                                    color="secondary"
                                    href="https://ksyos.nl/algemene-voorwaarden/"
                                    openInNewTab
                                    variant="externalLink"
                                >
                                    algemene voorwaarden.
                                </Link>
                            </Text>
                        </Collapse>
                        <Divider spacing="small" />
                        <Link
                            color="subtle"
                            fontWeight="normal"
                            iconLeft={isOpen ? 'caret-up' : 'caret-down'}
                            my={2}
                            onClick={() => setIsOpen(!isOpen)}
                            size="xs"
                            variant="button"
                        >
                            {isOpen
                                ? 'Lees minder over de berekening van de kosten'
                                : 'Lees meer over de berekening van de kosten'}
                        </Link>
                    </Box>
                </Card.Body>
            </Card>
        </>
    );
}
